/**
 * The storefront title band: white type on the navy `bg-hero` gradient (docs/15 §10,
 * approved OQ-1). Introduced on the public showcase and extracted here when the signed-in
 * pages adopted it, so the treatment is defined once rather than pasted per route.
 *
 * The §7 light-surface contrast trap doesn't apply — this band is dark by construction, so
 * the white heading and the `navy-100` subline are the intended pairing.
 *
 * `subline` is optional and callers pass it only when they have something true to say: a
 * count the page has actually loaded. A card that reads "0 teklif" while the list is still
 * fetching is worse than one that shows the title alone for a moment.
 *
 * **`photo` (M18).** The group site's banners carry a photograph behind the navy; ours were
 * flat, which is a large part of why the showcase read darker than the site it sits under
 * (`redesign-bidder.md` §B5). The image is layered **under** a gradient rather than swapped
 * for it, which keeps the white type legible on any photograph and means a missing file costs
 * nothing — the band renders as a plain navy veil. See `docs/ui-design/bidder-photography.md`.
 *
 * The veil is `--grad-hero-veil`, **not** the `--grad-hero` this band paints when it has no
 * photo. That one is 96%/85%/96% opaque — right for compositing over the page, wrong over a
 * photograph, where it let through 4–15% and the picture read as noise. The veil's alphas are
 * measured so white type clears 5:1 even over a pure-white photo (`contrast.test.ts`).
 *
 * The 4px brand-gradient rule along the bottom edge is the kit's own `.hero::after`
 * (`BIA360-Design-System.html`), the one place it puts brand colour against the navy. It was
 * never carried across when the band was built.
 */
export function PageHero({
  title,
  subline,
  photo,
}: {
  title: string;
  subline?: string;
  /** File under `public/img/` — pass a `PHOTOS` slot (`lib/photos.ts`), never a bare
   * filename. Omit for a plain navy band. */
  photo?: string;
}) {
  return (
    <div
      // `bg-navy-900` is the floor of the stack and the reason a broken photo is survivable:
      // an image that 404s drops its layer, and without an opaque colour underneath, the
      // *translucent* veil would sit straight on the page and repaint the band as washed-out
      // slate. The photo, when it loads, is opaque and hides this entirely.
      className="relative overflow-hidden rounded-lg bg-navy-900 bg-hero bg-cover bg-center px-6 py-10 text-white"
      // Photo first in the stack = painted *under* the gradient. Both live in one
      // `background-image` so the element keeps a single paint layer, and dropping `photo`
      // leaves the exact declaration the band shipped with.
      style={
        photo
          ? {
              backgroundImage: `var(--grad-hero-veil), url(/img/${photo})`,
            }
          : undefined
      }
    >
      <h1 className="font-head text-display font-semibold">{title}</h1>
      {/* The kit's `navy-100` subline is the intended pairing on the *opaque* band. Over a
          photograph it is not: through the veil, against a bright photo, it measures
          4.13–4.33:1 — under the 4.5:1 body-text floor that the white heading clears at
          5.12–5.36:1. So the photo case takes white, and loses nothing: at this size the
          hierarchy is carried by the display heading above it, not by 6% of grey. */}
      {subline && (
        <p className={`mt-1 text-sm ${photo ? 'text-white' : 'text-navy-100'}`}>{subline}</p>
      )}
      {/* The kit's brand rule (`.hero::after`) — decorative, so it stays out of the a11y tree. */}
      <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 bg-brand" />
    </div>
  );
}
